/**
 * AppShell — HeyMotek dashboard shell (Phase 1 React port).
 *
 * Replaces Layout.tsx. Matches the locked preview shell from
 * `~/heymotek-web/src/components/preview/dashboard/AppShell.astro`:
 *  - Desktop (≥md): left sidebar w/ 5-item nav (Dashboard / Contacts /
 *    Insights / Integrations / Account), coral active state, CSS
 *    text-stroke wordmark, sidebar footer with Logout.
 *  - Mobile (<md): top brand strip + bottom tab bar (5 tabs, iOS pattern).
 *
 * Preserved from Layout.tsx:
 *  - Admin "view as" impersonation banner + picker (desktop only in
 *    Phase 1 — mobile admin picker is a deferred minor feature)
 *  - EmailVerificationBanner (rendered inside main)
 *  - ErrorBoundary keyed by pathname so a crashed page doesn't bleed
 *    across navigation
 *  - Integrations needs-attention dot
 *  - Logout button (desktop sidebar footer; mobile users use Account
 *    page Sign-out — same as preview spec)
 *
 * Source of truth for visuals: brand sheet §6 (wordmark) + §4 (color
 * story). Plan: heymotek/STATUS/PLAN_DASHBOARD_REACT_PORT.md.
 */

import { useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Globe,
  Plug,
  User as UserIcon,
  LogOut,
  Eye,
  X,
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuth, startViewAs, stopViewAs } from '@/lib/useAuth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { EmailVerificationBanner } from '@/components/EmailVerificationBanner';
import { PageLayoutContext } from '@/lib/pageLayout';

interface NavItemSpec {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  showDot?: boolean;
}

/**
 * HeymotekWordmark — CSS text-stroke split per brand sheet §6.
 * Coral "motek" + outlined "hey". No SVG asset to maintain.
 */
function HeymotekWordmark({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'text-2xl' : 'text-xl';
  return (
    <span className={`font-extrabold ${sizeClass} leading-none tracking-tight`}>
      <span
        style={{
          WebkitTextStroke: '2px #0F172A',
          color: 'white',
          paintOrder: 'stroke fill',
        }}
      >
        hey
      </span>
      <span className="text-brand">motek</span>
    </span>
  );
}

function ViewAsPicker({
  clients,
  onChange,
}: {
  clients: { id: string; name: string }[];
  onChange: (clientId: string) => void;
}) {
  return (
    <Select value="" onValueChange={onChange}>
      <SelectTrigger className="w-full md:w-64">
        <SelectValue placeholder="Pick a client..." />
      </SelectTrigger>
      <SelectContent>
        {clients.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function isPathActive(itemPath: string, currentPathname: string): boolean {
  if (itemPath === '/') return currentPathname === '/';
  return currentPathname.startsWith(itemPath);
}

// Per-route max-width. Drives both the page content max-w AND the
// AppShell-level banner block (EmailVerification + GoLive), so banners
// align with the content beneath them on narrow pages (Account, Integrations).
// Pages no longer set their own `mx-auto max-w-*` — AppShell owns this.
const PAGE_MAX_WIDTHS: Record<string, string> = {
  '/': 'max-w-6xl',
  // Contacts: default for the common 5-column case. ContactsPage calls
  // usePageMaxWidth('max-w-screen-2xl') when the demo columns (Line +
  // Client) are visible — admin / Multi-WA Pro / Agency tier. The table
  // fills whichever width is active so the page never feels half-empty;
  // `overflow-x-auto` inside the card catches the rare too-wide case.
  '/contacts': 'max-w-7xl',
  '/insights': 'max-w-7xl',
  '/integrations': 'max-w-4xl',
  '/account': 'max-w-3xl',
};

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { me, clients, viewAsClientId, viewAsClientName } = useAuth();

  // Page can override its max-w via the usePageMaxWidth hook (drives both
  // the banner block + page content so they stay aligned). Cleared on
  // route change so the next page gets a fresh slate.
  const [pageMaxWidthOverride, setPageMaxWidthOverride] = useState<
    string | null
  >(null);
  const pageLayoutValue = useMemo(
    () => ({ setOverride: setPageMaxWidthOverride }),
    [],
  );

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/signin');
  };

  const handleViewAsChange = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) startViewAs(client.id, client.name);
  };

  const isAdmin = me?.is_system_admin ?? false;
  const impersonating = !!viewAsClientId;
  const adminWithoutImpersonation = isAdmin && !impersonating;

  // Integrations needs-attention dot (preserved from Layout.tsx).
  const googleNeedsAttention =
    !me?.google_ads_customer_id && !me?.google_ads_skipped_at;
  const facebookNeedsAttention =
    !me?.facebook_pixel_id && !me?.facebook_skipped_at;
  const integrationsNeedsAttention =
    !adminWithoutImpersonation &&
    (googleNeedsAttention || facebookNeedsAttention);

  const navItems: NavItemSpec[] = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { path: '/insights', label: 'Insights', icon: BarChart3 },
    { path: '/website', label: 'Website', icon: Globe },
    {
      path: '/integrations',
      label: 'Integrations',
      icon: Plug,
      showDot: integrationsNeedsAttention,
    },
    { path: '/account', label: 'Account', icon: UserIcon },
  ];

  return (
    <PageLayoutContext.Provider value={pageLayoutValue}>
    <div className="flex min-h-screen flex-col bg-bg-tint">
      {/* Impersonation banner (preserved from Layout.tsx) */}
      {impersonating && (
        <div className="flex items-center justify-center gap-3 bg-amber-100 px-4 py-2 text-sm font-medium text-amber-900">
          <Eye className="h-4 w-4" />
          <span className="truncate">
            Viewing as: {viewAsClientName ?? 'client'}
          </span>
          <button
            onClick={stopViewAs}
            className="inline-flex min-h-9 items-center gap-1 rounded bg-amber-200 px-2 py-1 text-xs text-amber-900 hover:bg-amber-300"
          >
            <X className="h-3 w-3" /> Exit
          </button>
        </div>
      )}

      {/* Mobile top brand strip — admin View-as picker lives here when
          admin && not impersonating && has clients (Phase 1 mobile parity
          with desktop top header). Hidden for normal users. */}
      <header className="sticky top-0 z-30 border-b border-dark/5 bg-white md:hidden">
        <div className="flex h-14 items-center justify-between gap-3 px-4">
          <Link to="/" className="flex shrink-0 items-center">
            <HeymotekWordmark size="sm" />
          </Link>
          {isAdmin && !impersonating && clients.length > 0 && (
            <div className="ml-auto min-w-0">
              <Select value="" onValueChange={handleViewAsChange}>
                <SelectTrigger
                  className="h-8 gap-1.5 border-dark/15 px-3 text-xs font-medium"
                  aria-label="View as a different client"
                >
                  <Eye className="h-3.5 w-3.5" />
                  <SelectValue placeholder="View as" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </header>

      <div className="md:flex md:flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden md:sticky md:top-0 md:flex md:h-screen md:w-60 md:shrink-0 md:flex-col md:border-r md:border-dark/5 md:bg-white">
          {/* Sidebar header — wordmark */}
          <div className="flex h-16 items-center border-b border-dark/5 px-6">
            <Link to="/" className="flex items-center">
              <HeymotekWordmark size="lg" />
            </Link>
          </div>

          {/* Sidebar nav */}
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isPathActive(item.path, location.pathname);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-brand text-white'
                      : 'text-muted-foreground hover:bg-dark/5 hover:text-dark'
                  }`}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {item.showDot && !isActive && (
                    <span
                      className="ml-auto h-2 w-2 shrink-0 rounded-full bg-amber-500"
                      aria-label="Has unconnected ad platforms"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Sidebar footer — Logout */}
          <div className="border-t border-dark/5 p-3">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-dark/5 hover:text-dark"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main content area */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Admin View-as header (desktop only — admin minority case) */}
          {isAdmin && !impersonating && clients.length > 0 && (
            <header className="hidden items-center justify-end gap-3 border-b bg-card px-6 py-3 md:flex">
              <span className="text-sm text-muted-foreground">View as:</span>
              <ViewAsPicker clients={clients} onChange={handleViewAsChange} />
            </header>
          )}

          <main className="flex-1 overflow-auto pb-20 md:pb-0">
            <div className="p-4 md:p-6">
              <div
                className={`mx-auto ${pageMaxWidthOverride ?? PAGE_MAX_WIDTHS[location.pathname] ?? 'max-w-7xl'}`}
              >
                <EmailVerificationBanner />
                {/* Keyed by pathname so a broken page's error state doesn't
                    bleed into the next route after navigation. */}
                <ErrorBoundary key={location.pathname}>
                  <Outlet />
                </ErrorBoundary>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-dark/5 bg-white md:hidden">
        <div className="grid grid-cols-5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path, location.pathname);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-1 py-2 text-xs font-bold transition-colors ${
                  isActive ? 'text-brand' : 'text-dark/70 active:text-dark'
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate text-xs">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
    </PageLayoutContext.Provider>
  );
}
